import type { PositionSummary } from "@papertiger/contracts";

/** Sortable data columns available across the position tables. */
export type PositionSortKey =
  | "market"
  | "venue"
  | "classifier"
  | "price"
  | "units"
  | "orderTime"
  | "orderType"
  | "fillTime"
  | "mark"
  | "unrealized"
  | "status"
  | "result"
  | "settlement"
  | "realized";

/** Supported ascending and descending table directions. */
export type SortDirection = "ascending" | "descending";

/** Current sort selection for one position table. */
export interface PositionSort {
  /** Selected data column. */
  key: PositionSortKey;
  /** Selected ordering direction. */
  direction: SortDirection;
}

/** Primitive value used to compare position rows. */
type PositionSortValue = string | number | null;

/** Locale-aware comparer for human-readable position labels. */
const positionLabelCollator = new Intl.Collator("en-US", {
  numeric: true,
  sensitivity: "base",
});

/**
 * Returns the units displayed for a position row.
 *
 * Open and pending positions show currently owned units. Fully closed positions
 * retain their lifetime acquired units instead of collapsing to zero.
 *
 * @param position Position to display.
 * @returns Current or lifetime acquired units.
 */
export function getPositionDisplayUnits(position: PositionSummary): number {
  return position.quantity > 0 ? position.quantity : position.acquiredQuantity;
}

/**
 * Advances a table sort selection after a column-header click.
 *
 * @param current Current table sort.
 * @param key Clicked data column.
 * @returns Updated sort selection.
 */
export function advancePositionSort(
  current: PositionSort,
  key: PositionSortKey,
): PositionSort {
  if (current.key !== key) {
    return { key, direction: "ascending" };
  }
  return {
    key,
    direction:
      current.direction === "ascending" ? "descending" : "ascending",
  };
}

/**
 * Returns a sorted copy of position rows.
 *
 * Null values always remain after populated values so missing execution data
 * cannot displace real values at the top of either sort direction.
 *
 * @param positions Position rows to sort.
 * @param sort Selected column and direction.
 * @returns Sorted copy of the input rows.
 */
export function sortPositions(
  positions: PositionSummary[],
  sort: PositionSort,
): PositionSummary[] {
  return positions
    .map((position, index) => ({ position, index }))
    .sort((left, right) => {
      const leftValue = getPositionSortValue(left.position, sort.key);
      const rightValue = getPositionSortValue(right.position, sort.key);
      const nullComparison = compareNullPlacement(leftValue, rightValue);
      if (nullComparison !== 0) {
        return nullComparison;
      }
      const comparison = comparePositionSortValues(leftValue, rightValue);
      if (comparison === 0) {
        return left.index - right.index;
      }
      return sort.direction === "ascending" ? comparison : -comparison;
    })
    .map(({ position }) => position);
}

/**
 * Resolves one comparable value from a position row.
 *
 * @param position Position being compared.
 * @param key Selected data column.
 * @returns Comparable primitive or null.
 */
function getPositionSortValue(
  position: PositionSummary,
  key: PositionSortKey,
): PositionSortValue {
  switch (key) {
    case "market":
      return position.marketTitle;
    case "venue":
      return position.venue;
    case "classifier":
      return position.outcomeName;
    case "price":
      return position.averageAcquisitionPricePercent1;
    case "units":
      return getPositionDisplayUnits(position);
    case "orderTime":
      return parseTimestamp(position.orderPlacedAt);
    case "orderType":
      return position.orderType;
    case "fillTime":
      return parseTimestamp(position.filledAt);
    case "mark":
      return position.markPricePercent1;
    case "unrealized":
      return position.unrealizedProfitDollars;
    case "status":
      return position.quantity === 0
        ? position.settledAt === null
          ? "sold"
          : "settled"
        : position.marketStatus;
    case "result":
      return position.realizedProfitDollars;
    case "settlement":
      return position.settlementPricePercent1;
    case "realized":
      return position.realizedProfitDollars;
  }
}

/**
 * Parses an API timestamp for numeric sorting.
 *
 * @param value ISO timestamp or null.
 * @returns Milliseconds since epoch or null.
 */
function parseTimestamp(value: string | null): number | null {
  if (value === null) return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
}

/**
 * Keeps null values after populated values in either direction.
 *
 * @param left Left comparison value.
 * @param right Right comparison value.
 * @returns Sort comparison result.
 */
function compareNullPlacement(
  left: PositionSortValue,
  right: PositionSortValue,
): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return 0;
}

/**
 * Compares two non-null sort values.
 *
 * @param left Left comparison value.
 * @param right Right comparison value.
 * @returns Sort comparison result.
 */
function comparePositionSortValues(
  left: PositionSortValue,
  right: PositionSortValue,
): number {
  if (left === null || right === null) return 0;
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }
  return positionLabelCollator.compare(String(left), String(right));
}
